/*
PURPOSE: Editorial artist avatar for AllInOneCoralCreator's editorial mode.
Mirrors the coral-design kit's CoralCreator Avatar: status reads from the avatar
itself —
  - searching (pending): a pulsing "?" placeholder on warm sand
  - nominate (not found / not yet on Coral): the gradient-initials avatar with a
    small terracotta "+" corner badge (we'll invite them)
  - matched (active): profile photo, else gradient-initials, + a terracotta check
    corner badge (reuses AddedTick)
  - disabled (ineligible): muted initials with a native-title reason, no badge
USE CASES:
- AllInOneCoralCreator (editorial=true) artist rows.
DOMAIN BOUNDARIES:
- Presentation only. Status is derived from the artist record's
  artistListItemStatus / artistListItemActive; the row's "Searching…" subtitle
  and the remove control live in the caller.
*/

import React from 'react';
import { Plus } from 'lucide-react';
import { ClientArtistListItem } from 'shared/types/platformTypes';
import AddedTick from '../AddedTick/AddedTick';

type AvatarStatus = 'matched' | 'searching' | 'nominate' | 'disabled';

function deriveStatus(artist: ClientArtistListItem): AvatarStatus {
  if (artist.artistListItemStatus === 'pending') return 'searching';
  if (artist.artistListItemStatus === 'not found') return 'nominate';
  if (artist.artistListItemActive === false) return 'disabled';
  return 'matched';
}

function disabledReason(artist: ClientArtistListItem): string {
  if (artist.artistListItemStatus === 'top-artist') {
    return 'This artist has reached high levels of commercial success and is not eligible for wearecoral.';
  }
  if (artist.artistListItemStatus === 'deceased') {
    return "wearecoral is for living artists as it's about supporting a thriving ecosystem.";
  }
  return 'This artist is not eligible for wearecoral.';
}

function initials(name?: string): string {
  if (!name) return '?';
  const letters = name
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return letters || '?';
}

interface EditorialArtistAvatarProps {
  artist: ClientArtistListItem;
  /** Tailwind size classes for the circle (default 36px). */
  sizeClassName?: string;
}

const RING = 'ring-2 ring-white shadow-sm';
// Small corner badge shared by the matched (check) and nominate (plus) states.
const BADGE_WRAP = 'absolute -right-0.5 -bottom-0.5 w-4 h-4 rounded-full ring-2 ring-white inline-flex items-center justify-center';

const EditorialArtistAvatar: React.FC<EditorialArtistAvatarProps> = ({
  artist,
  sizeClassName = 'w-9 h-9',
}) => {
  const status = deriveStatus(artist);

  // Searching: a full pulsing "?" placeholder — no identity resolved yet.
  if (status === 'searching') {
    return (
      <span
        className={`${sizeClassName} rounded-full flex items-center justify-center ${RING} bg-[#E8DEC9] text-editorial-muted/55 font-bold text-base animate-pulse flex-none`}
        aria-label="Searching"
      >
        ?
      </span>
    );
  }

  // Matched / nominate / disabled all share the gradient-initials (or photo)
  // avatar; the corner badge distinguishes them.
  const imageUrl = artist.artistData?.imageUrl;
  const isDisabled = status === 'disabled';

  const face = imageUrl ? (
    <img
      src={imageUrl}
      alt={artist.artistName || 'Artist'}
      className={`${sizeClassName} rounded-full object-cover ${RING}`}
    />
  ) : (
    <span
      className={`${sizeClassName} rounded-full flex items-center justify-center ${RING} text-white font-bold text-[11px]`}
      style={{ background: 'linear-gradient(135deg, #FC5373, #FDBD80)' }}
      aria-hidden="true"
    >
      {initials(artist.artistName)}
    </span>
  );

  return (
    <span
      className={`relative inline-flex flex-none ${sizeClassName} ${isDisabled ? 'opacity-50' : ''}`}
      {...(isDisabled ? { title: disabledReason(artist) } : {})}
    >
      {face}
      {status === 'matched' && (
        <span className={`${BADGE_WRAP} bg-white`}>
          <AddedTick colorClassName="text-editorial-accent" sizeClassName="w-4 h-4" ariaLabel="Backed" />
        </span>
      )}
      {status === 'nominate' && (
        <span className={`${BADGE_WRAP} bg-editorial-accent`} aria-label="Not on Coral yet — we'll invite them">
          <Plus size={10} strokeWidth={3} className="text-white" />
        </span>
      )}
    </span>
  );
};

export default EditorialArtistAvatar;
